import React from "react";
import { Container, RadioButtonWithLabel } from "../../../components";
import { Text, Button } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { BankIdRefererType } from "../../../../../constants";
import { AnnouncementType, RootState } from "../../../../../models";
import { BusinessDetailsDetails } from "../../../../../models/state/business";
import { RejectReasonID } from "../../../../../models/transfer";
import { getOrderItemGuid, isPortin } from "../../../app/state/selectors";
import {
    getCustomer as getBankIdCustomer,
    isBankIdVerified as isBankIdVerifiedSelector,
} from "../../../authentication/state/selectors";
import BankIdSection from "../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import OrgOrNameInput from "../../../checkout/components/molecules/OrgOrNameInput/OrgOrNameInput";
import { checkOrgName, checkOrgNumber } from "../../../checkout/state/actions/businessActions";
import {
    resetVerificationFromFormerLegal,
    updateFormerLegalB2B,
} from "../../../checkout/state/actions/transferOwnershipActions";
import {
    getOtpVerifiedPhoneNumber,
    getSelectedFormerLegalOrganization,
    getSelectedPhoneNumber,
    getTransferExistingIndividualLegalOwnerFullName,
    getTransferExistingOrganizationName,
    getTransferNewIndividualLegalOwnerFullName,
    getTransferNewLegalOwnerField,
} from "../../../checkout/state/selectors/accountSelectors";
import { getBrregNames } from "../../../checkout/state/selectors/businessDetailsSelectors";
import { getFormattedPhoneNumber } from "../../../rapidform/formValidation";
import TEXT from "../../text-consts";
import OtpVerification from "./OtpVerification";

type AcceptType = "accept-b2b" | "accept-b2c";

interface AcceptContentProps {
    isB2BTransfer: boolean;
    setRejectReasons: React.Dispatch<React.SetStateAction<RejectReasonID | null>>;
    setSubSelectedOption: React.Dispatch<React.SetStateAction<AcceptType | null>>;
    subSelectedOption: string | null;
}

const AcceptContent: React.FC<AcceptContentProps> = ({
    isB2BTransfer,
    subSelectedOption,
    setSubSelectedOption,
    setRejectReasons,
}) => {
    const dispatch = useDispatch();
    const newLegalOrganizationName = useSelector((state: RootState) =>
        getTransferNewLegalOwnerField(state, "organization", "name"),
    );
    const newLegalOwnerIndividualName = useSelector((state: RootState) =>
        getTransferNewIndividualLegalOwnerFullName(state),
    );
    const selectedPhoneNumber = useSelector((state: RootState) => getSelectedPhoneNumber(state));
    const isBankIdVerified = useSelector((state: RootState) => isBankIdVerifiedSelector(state));
    const existingLegalOwner = useSelector(
        (state: RootState) => getTransferExistingIndividualLegalOwnerFullName(state) || "",
    );

    const bankIdCustomer = useSelector((state: RootState) => getBankIdCustomer(state));
    const formerLegalOrgName = useSelector((state: RootState) => getTransferExistingOrganizationName(state) || "");
    const selectedOrganization = useSelector((state: RootState) => getSelectedFormerLegalOrganization(state));
    const otpVerifiedPhoneNumber = useSelector((state: RootState) => getOtpVerifiedPhoneNumber(state));
    const isPortinOrder = useSelector((state: RootState) => isPortin(state));
    const isOrganizationConsent = subSelectedOption === "accept-b2b";
    const isVerificationStarted = isBankIdVerified || !!otpVerifiedPhoneNumber;

    const bankIdOptions = {
        referer: "consentFormerLegal" as BankIdRefererType,
        typeId: "consent_former_legal" as AnnouncementType,
        skipDisplayHeading: true,
        skipDisplayError: true,
        skipDisplayDescription: true,
    };

    // On behalf of an organization: select organization -> (transfer only) one-time code to a number in the organization -> BankID
    const requiresOtp = isOrganizationConsent && !isPortinOrder;
    const shouldShowOtp = requiresOtp && !!selectedOrganization && !otpVerifiedPhoneNumber;
    const shouldShowBankId =
        !isBankIdVerified &&
        (subSelectedOption === "accept-b2c" ||
            (isOrganizationConsent && !!selectedOrganization && (!requiresOtp || !!otpVerifiedPhoneNumber)));

    const nameOfPersonFromBankId = existingLegalOwner || bankIdCustomer;

    return (
        <div className="consent-landing-page__consent-expanded">
            {!isBankIdVerified && (
                <section>
                    <div
                        className="paragraph"
                        dangerouslySetInnerHTML={{
                            __html: TEXT.CONSENT_FORMER_LEGAL.IDENTIFICATION_TEXT(
                                isB2BTransfer ? newLegalOrganizationName : newLegalOwnerIndividualName,
                                getFormattedPhoneNumber(selectedPhoneNumber),
                            ),
                        }}
                        data-di-mask
                    />
                </section>
            )}
            {isBankIdVerified && !!nameOfPersonFromBankId && (
                <>
                    <Text mb="100" variant="additional-100">
                        {TEXT.CONSENT_FORMER_LEGAL.BANK_ID_VERIFIED}
                    </Text>
                    <div className="consent-landing-page__consent-expanded bankid-information" data-di-mask>
                        <Text mb="100" variant="additional-100" className="telia-sans-medium">
                            {nameOfPersonFromBankId}
                        </Text>
                    </div>
                </>
            )}
            <div className="consent-landing-page__consent-option-b2b">
                {(isPortinOrder || !!formerLegalOrgName) && (
                    <SubSelectionOptionB2B
                        checked={subSelectedOption === "accept-b2b"}
                        onChange={(event) => {
                            setSubSelectedOption(event.target.value as AcceptType);
                            setRejectReasons(null);
                        }}
                        isLocked={isVerificationStarted}
                    />
                )}
                {!isBankIdVerified && isPortinOrder && (
                    <RadioButtonWithLabel
                        value="accept-b2c"
                        onChange={(event) => {
                            setSubSelectedOption(event.target.value as AcceptType);
                            setRejectReasons(null);
                        }}
                        name="tlo-accept-b2b"
                        checked={subSelectedOption === "accept-b2c"}
                        label="Jeg godkjenner som privatperson"
                    />
                )}
                {isOrganizationConsent && !!otpVerifiedPhoneNumber && (
                    <Text mb="100" variant="additional-100" data-di-mask>
                        {TEXT.CONSENT_FORMER_LEGAL.OTP.VERIFIED(getFormattedPhoneNumber(otpVerifiedPhoneNumber))}
                    </Text>
                )}
                {isVerificationStarted &&
                    (subSelectedOption === "accept-b2b" || subSelectedOption === "accept-b2c") && (
                        <div className="consent-landing-page__start-over-container">
                            <Text mb="100">
                                {TEXT.CONSENT_FORMER_LEGAL.START_OVER(subSelectedOption === "accept-b2b")}
                            </Text>
                            <div className="consent-landing-page__button-wrapper">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => {
                                        setSubSelectedOption(
                                            !isPortinOrder && !formerLegalOrgName ? "accept-b2c" : null,
                                        );
                                        dispatch(resetVerificationFromFormerLegal());
                                    }}
                                >
                                    Start på nytt
                                </Button>
                            </div>
                        </div>
                    )}
            </div>
            {shouldShowOtp && <OtpVerification />}
            {shouldShowBankId && (
                <Container className="consent-landing-page__bankid-container">
                    <BankIdSection {...bankIdOptions} />
                </Container>
            )}
        </div>
    );
};

export default AcceptContent;

const SubSelectionOptionB2B = ({
    checked,
    onChange,
    isLocked,
}: {
    checked: boolean;
    isLocked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
    const brregNameSearch = useSelector((state: RootState) => getBrregNames(state));
    const orderItemGuid = useSelector((state: RootState) => getOrderItemGuid(state));
    const selectedOrganization = useSelector((state: RootState) => getSelectedFormerLegalOrganization(state));
    const registeredOrganizationName = useSelector((state: RootState) => getTransferExistingOrganizationName(state));
    const isAtEnvironment = useSelector((state: RootState) => state.config?.environment === "at");

    const dispatch = useDispatch();

    const lookupOrganization = async (orgNumber: string): Promise<Partial<BusinessDetailsDetails>> => {
        try {
            return await dispatch(checkOrgNumber(orgNumber, undefined, { flashOnError: !isAtEnvironment }));
        } catch (err) {
            // test companies used in AT are not registered in Brreg - the org number is still verified by the server
            if (!isAtEnvironment) throw err;
            return { orgNumber, businessName: registeredOrganizationName || orgNumber };
        }
    };

    const selectedOrganizationText = selectedOrganization && (
        <div className="consent-landing-page__selected-company" data-di-mask>
            Valgt organisasjon: <span className="telia-sans-medium">{selectedOrganization.name}</span> (org.nr.:{" "}
            {selectedOrganization.number})
        </div>
    );

    if (isLocked) {
        return checked ? selectedOrganizationText || null : null;
    }

    return (
        <>
            <RadioButtonWithLabel
                value="accept-b2b"
                onChange={onChange}
                name="tlo-accept-b2b"
                checked={checked}
                label="Jeg godkjenner på vegne av en bedrift"
            />
            {checked && (
                <div className="consent-landing-page__subaccept-company">
                    <OrgOrNameInput
                        checkOrgName={(name) => dispatch(checkOrgName(name))}
                        checkOrgNumber={async (orgNumber) => {
                            const org = await lookupOrganization(orgNumber);

                            if (org) {
                                const orgName = org.businessName || "";
                                const organizationNumber = org.orgNumber || "";
                                if (orgName !== "" && organizationNumber !== "") {
                                    await dispatch(updateFormerLegalB2B(orderItemGuid, orgName, organizationNumber));
                                }
                            }
                        }}
                        businesses={brregNameSearch}
                        data-di-mask
                    />
                    {selectedOrganizationText}
                </div>
            )}
        </>
    );
};
